import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { WorkflowShell, type WorkflowStep, type ViewAsOption } from '@powercore/ui-blocks';
import { ToastProvider } from '@powercore/ui';
import { apiRequest, setViewAsUser } from './platform/api';
import {
  Settings,
  Shield,
  Sparkles,
  Store,
  Library,
  Building2,
} from 'lucide-react';

// Platform
import { useAuth, AuthCallback } from './platform';

// Pages
import {
  DashboardPage,
  SettingsPage,
  FilesPage,
  AuthPage,
  SourcesPage,
  KnowledgePage,
  PostsPage,
  PublishPage,
  OnboardingPage,
  AdminPromptsPage,
  AdminInviteUserPage,
  AdminDashboardPage,
  AdminModelSettingsPage,
  PricingPage,
  CompanyProfilePage,
  MarketplacePage,
  SkillDetailPage,
  MySkillsPage,
  AdminSkillsPage,
} from './pages';
import {
  AdminUsersPage,
  AdminUserCreatePage,
  AdminUserEditPage,
  AdminBillingPage,
  AdminPerformancePage,
  AdminPricingPage,
  AdminAnalyticsPage,
  AdminEmailTemplatesPage,
  AdminReleaseNotesPage,
  AdminWaitlistPage,
} from './pages/admin';
import { HowToSourcesPage } from './pages/HowToSourcesPage';
import { JoinWaitlistPage } from './pages/JoinWaitlistPage';
import { TryPage } from './pages/TryPage';
import { AdminLayout } from './layouts/AdminLayout';
import { AuthGuard } from './components/AuthGuard';
import { EvolvingCurtain } from './components/EvolvingCurtain';
import { BillingStatusBadge } from './components/BillingStatusBadge';
import { ErrorBoundary } from './components/ErrorBoundary';
import { WhatsNewPopup } from './components/WhatsNewPopup';

// Mazo (Skills Store) nav: Browse Skills > My Skills
const workflowStepDefinitions = [
  { id: 'browse', label: 'Browse Skills', icon: <Store className="h-4 w-4" />, path: '/' },
  { id: 'my-skills', label: 'My Skills', icon: <Library className="h-4 w-4" />, path: '/my-skills' },
  { id: 'company', label: 'Your Company', icon: <Building2 className="h-4 w-4" />, path: '/company' },
];

// Admin navigation removed - now handled by AdminLayout

// Admin Routes Component - uses AdminLayout instead of WorkflowShell
function AdminRoutes() {
  return (
    <AuthGuard>
      <AdminLayout>
        <Routes>
          <Route path="/" element={<AdminDashboardPage />} />
          <Route path="/skills" element={<AdminSkillsPage />} />
          <Route path="/analytics" element={<AdminAnalyticsPage />} />
          <Route path="/users" element={<AdminUsersPage />} />
          <Route path="/users/create" element={<AdminUserCreatePage />} />
          <Route path="/users/:userId/edit" element={<AdminUserEditPage />} />
          <Route path="/users/invite" element={<AdminInviteUserPage />} />
          <Route path="/prompts" element={<AdminPromptsPage />} />
          <Route path="/settings/models" element={<AdminModelSettingsPage />} />
          <Route path="/billing" element={<AdminBillingPage />} />
          <Route path="/pricing" element={<AdminPricingPage />} />
          <Route path="/performance" element={<AdminPerformancePage />} />
          <Route path="/email-templates" element={<AdminEmailTemplatesPage />} />
          <Route path="/release-notes" element={<AdminReleaseNotesPage />} />
          <Route path="/waitlist" element={<AdminWaitlistPage />} />
        </Routes>
      </AdminLayout>
    </AuthGuard>
  );
}

function AppLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading, user, signIn, signOut } = useAuth();
  const [viewingAs, setViewingAs] = useState<ViewAsOption | null>(null);
  const [viewAsOptions, setViewAsOptions] = useState<ViewAsOption[]>([]);
  const [isUserAdmin, setIsUserAdmin] = useState(false);
  // Early-access gate: null = unknown, true/false once /api/me resolves.
  const [hasAccess, setHasAccess] = useState<boolean | null>(null);
  const [isAppReady, setIsAppReady] = useState(false);
  const [billingData, setBillingData] = useState<{
    planName: string;
    planId: string;
    usage: Array<{ featureId: string; featureName: string; usage: number; limit: number }>;
    trialDaysRemaining?: number | null;
    trialExpired?: boolean;
  } | null>(null);
  const [releaseNote, setReleaseNote] = useState<{
    version: string;
    title: string;
    features: Array<{ title: string; description: string }>;
  } | null>(null);
  const [showReleasePopup, setShowReleasePopup] = useState(false);
  const [latestRelease, setLatestRelease] = useState<{
    version: string;
    title: string;
    features: Array<{ title: string; description: string }>;
  } | null>(null);
  const [showLinkedInPrompt, setShowLinkedInPrompt] = useState(false);

  // Memoize navigation callbacks to prevent re-creating on every render
  const navigateToSettings = useCallback(() => navigate('/settings'), [navigate]);
  const handleSignOut = useCallback(() => signOut(), [signOut]);

  // Track if we've already checked onboarding status this session
  const hasCheckedOnboardingRef = useRef(false);

  // Track if the initial auth check has completed — prevents subsequent isLoading
  // toggles (from Logto token refreshes) from unmounting the entire app
  const hasCompletedInitialAuthRef = useRef(false);

  // Fetch billing status for header badge
  const loadBilling = useCallback(async () => {
    try {
      const [statusRes, usageRes, trialRes] = await Promise.all([
        apiRequest<{ data: { subscription: { plan_id: string } | null; plan: { name: string } | null } }>('/api/billing/status'),
        apiRequest<{ data: Array<{ featureId: string; featureName: string; usage: number; limit: number }> }>('/api/billing/usage'),
        apiRequest<{ data: { isTrial: boolean; trialExpired: boolean; daysRemaining: number | null } }>('/api/billing/trial-status').catch(() => null),
      ]);
      const planId = statusRes.data?.subscription?.plan_id || 'free';
      const planName = statusRes.data?.plan?.name || 'Free';
      const usage = usageRes.data || [];
      const trialData = trialRes?.data;
      setBillingData({
        planName,
        planId,
        usage,
        trialDaysRemaining: trialData?.isTrial ? trialData.daysRemaining : null,
        trialExpired: trialData?.trialExpired ?? false,
      });
    } catch (err) {
      console.error('Failed to load billing status:', err);
    }
  }, []);

  // Listen for billing updates (e.g. after checkout verification)
  useEffect(() => {
    window.addEventListener('billing-updated', loadBilling);
    return () => window.removeEventListener('billing-updated', loadBilling);
  }, [loadBilling]);

  // Sync view-as state to API client synchronously (before React re-renders)
  // so the header is set before child components remount and fetch data
  const handleViewAsChange = useCallback((option: ViewAsOption | null) => {
    setViewAsUser(option?.id || null);
    setViewingAs(option);
  }, []);

  // Check if user is admin and load admin data if so
  // Also check onboarding status for non-admin users (only once per session)
  useEffect(() => {
    if (isAuthenticated) {
      const checkAdminAndLoadUsers = async () => {
        try {
          // First check if user is admin (and early-access status) via /api/me
          const meResult = await apiRequest<{ isAdmin: boolean; hasAccess?: boolean }>('/api/me', {
            method: 'GET',
          });

          const isAdmin = meResult.isAdmin ?? false;
          setIsUserAdmin(isAdmin);

          // Early-access gate: admins always allowed; otherwise honor hasAccess.
          const access = isAdmin || (meResult.hasAccess ?? false);
          setHasAccess(access);

          // Check onboarding status for allowlisted non-admin users only.
          // Non-allowlisted users see the "Mazo is evolving" curtain instead.
          // Skip if already on onboarding page or if we've already checked.
          if (access && !isAdmin && !hasCheckedOnboardingRef.current && !window.location.pathname.startsWith('/onboarding')) {
            hasCheckedOnboardingRef.current = true;
            try {
              const onboardingResult = await apiRequest<{ data: { completed: boolean } }>('/api/onboarding/status', {
                method: 'GET',
              });
              if (onboardingResult.data && !onboardingResult.data.completed) {
                // Redirect to onboarding if not completed
                navigate('/onboarding');
              }
            } catch (onboardingErr) {
              console.error('Failed to check onboarding status:', onboardingErr);
            }
          }

          // Only load users for view-as dropdown if user is admin
          if (isAdmin) {
            const result = await apiRequest<{ data: Array<{ user_id: string; logto: { name: string; primaryEmail: string } | null }> }>('/api/admin/users', {
              method: 'GET',
            });

            if (result.data) {
              const options: ViewAsOption[] = result.data.map(u => ({
                id: u.user_id,
                label: u.logto?.name || u.logto?.primaryEmail || 'Unknown User',
                description: u.logto?.primaryEmail || '',
                type: 'user' as const,
              }));
              setViewAsOptions(options);
            }
          }
        } catch (err) {
          console.error('Failed to check admin status:', err);
          setIsUserAdmin(false);
        } finally {
          setIsAppReady(true);
        }
      };
      checkAdminAndLoadUsers();

      // Fetch billing status for header badge
      loadBilling();
    } else {
      setIsUserAdmin(false);
      setHasAccess(null);
      setIsAppReady(false);
      setViewAsOptions([]);
      setBillingData(null);
      // Reset the onboarding check flag when user logs out
      hasCheckedOnboardingRef.current = false;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, navigate, loadBilling]);

  // Check for new release notes after app is ready
  useEffect(() => {
    if (!isAuthenticated || !isAppReady) return;

    const checkReleaseNotes = async () => {
      try {
        const result = await apiRequest<{
          data: { version: string; title: string; features: Array<{ title: string; description: string }> };
        }>('/api/release-notes/latest');
        const note = (result as any)?.data || result;
        if (note && note.version) {
          const noteData = {
            version: note.version,
            title: note.title,
            features: note.features || [],
          };
          setLatestRelease(noteData);
          const lastSeen = localStorage.getItem('mazo_last_seen_release');
          if (lastSeen !== note.version) {
            setReleaseNote(noteData);
            setShowReleasePopup(true);
          }
        }
      } catch {
        // Non-critical — don't block the app
      }
    };

    checkReleaseNotes();
  }, [isAuthenticated, isAppReady]);

  // Prompt to connect LinkedIn (show once per user if not connected)
  useEffect(() => {
    if (!isAuthenticated || !isAppReady) return;
    const dismissed = localStorage.getItem('mazo_linkedin_prompt_dismissed');
    if (dismissed) return;

    apiRequest<{ data: { connected: boolean } }>('/api/linkedin/status')
      .then(result => {
        const data = (result as any)?.data || result;
        if (!data?.connected) {
          // Delay slightly so release notes popup shows first
          setTimeout(() => setShowLinkedInPrompt(true), 1500);
        }
      })
      .catch(() => {});
  }, [isAuthenticated, isAppReady]);

  // Determine active step based on current path
  const activeStepId = useMemo(() => {
    const path = location.pathname;
    if (path.startsWith('/company')) return 'company';
    if (path.startsWith('/my-skills')) return 'my-skills';
    // Skill detail pages keep "Browse" highlighted; legacy content pages highlight nothing
    if (path.startsWith('/sources') || path.startsWith('/publish') || path.startsWith('/posts') || path.startsWith('/knowledge')) return null;
    return 'browse';
  }, [location.pathname]);

  // Memoize workflow steps to prevent re-creating on every render
  const workflowSteps: WorkflowStep[] = useMemo(() => {
    return workflowStepDefinitions.map(step => ({
      ...step,
      active: step.id === activeStepId,
      onClick: () => navigate(step.path),
    }));
  }, [activeStepId, navigate]);

  // Memoize user display to prevent re-creating on every render
  const userDisplay = useMemo(() => {
    if (isAuthenticated) {
      const menuItems = [
        {
          label: 'Profile',
          icon: <Settings className="h-4 w-4" />,
          onClick: navigateToSettings,
        },
        {
          label: "What's New",
          icon: <Sparkles className="h-4 w-4" />,
          onClick: async () => {
            if (latestRelease) {
              setReleaseNote(latestRelease);
              setShowReleasePopup(true);
            } else {
              try {
                const result = await apiRequest<{
                  data: { version: string; title: string; features: Array<{ title: string; description: string }> };
                }>('/api/release-notes/latest');
                const note = (result as any)?.data || result;
                if (note && note.version) {
                  const noteData = { version: note.version, title: note.title, features: note.features || [] };
                  setLatestRelease(noteData);
                  setReleaseNote(noteData);
                  setShowReleasePopup(true);
                }
              } catch {
                // No release notes available
              }
            }
          },
        },
        {
          label: 'Sign Out',
          onClick: handleSignOut,
          destructive: true,
        },
      ];

      // Only add Admin menu items for admin users
      if (isUserAdmin) {
        menuItems.unshift(
          {
            label: 'Admin',
            icon: <Shield className="h-4 w-4" />,
            onClick: () => {
              console.log('Admin clicked, navigating to /admin');
              navigate('/admin/users');
            },
          },
          {
            label: 'Full Profile Reset',
            onClick: async () => {
              if (!user?.id) return;
              if (!confirm('This will delete ALL your posts, sources, knowledge, voice profile, and reset onboarding. Are you sure?')) return;
              try {
                await apiRequest(`/api/admin/users/${user.id}/reset`, { method: 'POST' });
                sessionStorage.clear();
                navigate('/onboarding');
                window.location.reload();
              } catch (err) {
                console.error('Profile reset failed:', err);
                alert('Reset failed. Check console for details.');
              }
            },
            destructive: true,
          },
        );
      }

      return {
        name: user?.name || 'User',
        email: user?.email || '',
        isAdmin: false, // Admin uses separate /admin route — hide WorkflowShell admin sidebar button
        viewingAs: viewingAs ?? undefined,
        viewAsOptions: isUserAdmin ? viewAsOptions : [],
        onViewAsChange: handleViewAsChange,
        menuItems,
      };
    }
    return {
      name: 'Guest',
      email: '',
      isAdmin: false,
      menuItems: [],
    };
  }, [isAuthenticated, user?.name, user?.email, isUserAdmin, viewingAs, viewAsOptions, navigate, navigateToSettings, handleSignOut, handleViewAsChange]);

  // Show loading ONLY during the initial auth check.
  // Once the app has loaded, never show the loading spinner again — subsequent
  // isLoading toggles (from Logto token refreshes triggered by API calls) must NOT
  // unmount the entire app tree, which would destroy all page state.
  if (!isLoading) {
    hasCompletedInitialAuthRef.current = true;
  }
  if (isLoading && !hasCompletedInitialAuthRef.current) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="mx-auto h-8 w-8 animate-spin rounded-full border-b-2 border-mazo-600" />
          <p className="mt-4 text-gray-500" style={{ fontFamily: "'Outfit', sans-serif" }}>Loading...</p>
        </div>
      </div>
    );
  }

  // Public face during the Skills Marketplace rollout: logged-out visitors see
  // the "Mazo is evolving" curtain with a discreet sign-in for early-access users.
  const publicPaths = ['/auth', '/callback'];
  const isPublicPath = publicPaths.some(p => location.pathname.startsWith(p));
  if (!isAuthenticated && !isPublicPath) {
    return <EvolvingCurtain mode="signed-out" onSignIn={() => signIn()} />;
  }

  // Show loading screen while checking onboarding status (prevents flash of empty PostsPage)
  if (isAuthenticated && !isAppReady) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-center">
          <img src="/logo.svg" alt="Mazo" className="mx-auto mb-4 h-12 w-12" />
          <div className="mx-auto mt-4 h-8 w-8 animate-spin rounded-full border-b-2 border-mazo-600" />
          <p className="mt-4 text-gray-500" style={{ fontFamily: "'Outfit', sans-serif" }}>Setting up your workspace...</p>
        </div>
      </div>
    );
  }

  // Authenticated but not on the early-access allowlist → "Mazo is evolving".
  if (isAuthenticated && hasAccess === false) {
    return <EvolvingCurtain mode="no-access" email={user?.email} onSignOut={() => signOut()} />;
  }

  return (
    <>
    {showReleasePopup && releaseNote && (
      <WhatsNewPopup
        version={releaseNote.version}
        title={releaseNote.title}
        features={releaseNote.features}
        onDismiss={() => {
          localStorage.setItem('mazo_last_seen_release', releaseNote.version);
          setShowReleasePopup(false);
        }}
      />
    )}
    {showLinkedInPrompt && !showReleasePopup && (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9998]" onClick={() => { localStorage.setItem('mazo_linkedin_prompt_dismissed', '1'); setShowLinkedInPrompt(false); }}>
        <div className="bg-white rounded-2xl shadow-xl max-w-md w-full mx-4 p-6" onClick={e => e.stopPropagation()}>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-[#0A66C2] rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900">Connect LinkedIn</h3>
          </div>
          <p className="text-sm text-gray-600 mb-2">
            Publish your posts directly to LinkedIn with one click — no more copy-pasting.
          </p>
          <p className="text-xs text-gray-400 mb-5">
            Connect once and publish from Mazo whenever you're ready.
          </p>
          <div className="flex gap-3">
            <button
              onClick={() => { localStorage.setItem('mazo_linkedin_prompt_dismissed', '1'); setShowLinkedInPrompt(false); }}
              className="flex-1 px-4 py-2.5 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Maybe later
            </button>
            <button
              onClick={() => { localStorage.setItem('mazo_linkedin_prompt_dismissed', '1'); setShowLinkedInPrompt(false); navigate('/settings'); }}
              className="flex-1 px-4 py-2.5 text-sm text-white bg-[#0A66C2] rounded-lg hover:bg-[#004182] transition-colors font-medium"
            >
              Connect LinkedIn
            </button>
          </div>
        </div>
      </div>
    )}
    <WorkflowShell
      logo={
        <div
          onClick={() => navigate('/')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            cursor: 'pointer',
          }}
        >
          <img src="/logo.svg" alt="Mazo" style={{ width: '1.75rem', height: '1.75rem' }} />
          <span style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 500, fontSize: '1.25rem', color: 'var(--pc-fg-base)' }}>
            Mazo
          </span>
        </div>
      }
      workflowSteps={workflowSteps}
      user={userDisplay}
      headerActions={billingData ? (
        <BillingStatusBadge
          planName={billingData.planName}
          planId={billingData.planId}
          usage={billingData.usage}
          onUpgradeClick={() => navigate('/pricing')}
          trialDaysRemaining={billingData.trialDaysRemaining}
          trialExpired={billingData.trialExpired}
        />
      ) : undefined}
    >
      {viewingAs && (
        <div
          style={{
            position: 'sticky',
            top: 0,
            zIndex: 50,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.75rem',
            padding: '0.5rem 1rem',
            backgroundColor: '#f59e0b',
            color: '#1a1a1a',
            fontSize: '0.875rem',
            fontWeight: 500,
            fontFamily: "'Outfit', sans-serif",
          }}
        >
          <span>Viewing as: {viewingAs.label}</span>
          <button
            onClick={() => handleViewAsChange(null)}
            style={{
              padding: '0.25rem 0.75rem',
              borderRadius: '0.375rem',
              backgroundColor: 'rgba(0,0,0,0.15)',
              border: 'none',
              cursor: 'pointer',
              fontSize: '0.8125rem',
              fontWeight: 500,
              color: '#1a1a1a',
            }}
          >
            Return to Admin View
          </button>
        </div>
      )}
      <div key={viewingAs?.id || 'self'}>
        <Routes>
          {/* Skills marketplace (primary product) */}
          <Route path="/" element={<MarketplacePage />} />
          <Route path="/skills/:slug" element={<SkillDetailPage />} />
          <Route path="/my-skills" element={<MySkillsPage />} />
          <Route path="/company" element={<CompanyProfilePage />} />

          {/* Legacy content-platform pages (kept reachable, not in nav) */}
          <Route path="/posts" element={<PostsPage />} />
          <Route path="/sources" element={<SourcesPage />} />
          <Route path="/knowledge" element={<KnowledgePage />} />
          <Route path="/publish" element={<PublishPage />} />

          {/* Settings & Auth */}
          <Route path="/auth" element={<AuthPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/onboarding" element={<OnboardingPage />} />

          {/* Legacy routes */}
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/files" element={<FilesPage />} />
        </Routes>
      </div>
    </WorkflowShell>
    </>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <ToastProvider>
        <Routes>
          <Route path="/callback" element={<AuthCallback />} />
          <Route path="/how-to/sources" element={<HowToSourcesPage />} />
          <Route path="/join" element={<JoinWaitlistPage />} />
          <Route path="/try" element={<TryPage />} />
          <Route path="/admin/*" element={<AdminRoutes />} />
          <Route path="/*" element={<AppLayout />} />
        </Routes>
      </ToastProvider>
    </ErrorBoundary>
  );
}
